import { type CartDiscountReference } from '@commercetools/platform-sdk'

import { getLocaleCodeFromCountryLang } from '@src/i18n/locale.util'
import type { LocaleCode } from '@src/i18n/locales'
import {
  formatCartAmount,
  getCanadianTaxLabel,
  getComputedDetailsLabels,
  getLineItemAttribute,
  getLineItemImages,
  getLineItemName,
} from '@src/utils/cart.util'

export interface GetLineItemInfoArgs {
  lineItem: Commercetools.LineItem | undefined | null
  country: string
  lang: string
  t: (key: string | undefined, tokens?: Record<string, string | undefined>) => string
  quantity?: number
}

export function getLineItemPrices(args: {
  lineItem: Commercetools.LineItem | undefined | null
  t: (key: string | undefined, tokens?: Record<string, string | undefined>) => string
  quantity?: number
}) {
  const { lineItem, t, quantity: quantityArg } = args

  const quantity = quantityArg ?? lineItem?.quantity ?? 1

  const unitPrice = lineItem?.price?.value?.centAmount as number
  let totalPriceDiscounted: number | undefined = undefined

  // lineItem.discountedPrice consists of either:
  // only cart discounts or
  // cart discounts AND product discounts, if the latter exists
  const hasCartDiscount = Boolean(lineItem?.discountedPricePerQuantity?.length)
  const hasProductDiscount = Boolean(lineItem?.price?.discounted?.value)

  if (hasCartDiscount) {
    // The unit price may be different for each quantity of a line item
    totalPriceDiscounted = lineItem?.discountedPricePerQuantity?.reduce<number | undefined>(
      (acc, quantityPrice) => {
        const unitPrice = quantityPrice.discountedPrice.value.centAmount
        const price = unitPrice * (quantityPrice.quantity || 0)
        return (acc ?? 0) + price
      },
      undefined
    )
  } else if (hasProductDiscount) {
    const discountedUnitPrice = lineItem?.price?.discounted?.value?.centAmount
    totalPriceDiscounted = discountedUnitPrice! * quantity
  }

  return {
    totalPrice: unitPrice * quantity,
    totalPriceFormatted: formatCartAmount(unitPrice * quantity, t),
    totalPriceDiscounted: totalPriceDiscounted ?? undefined,
    totalPriceDiscountedFormatted:
      totalPriceDiscounted === undefined ? undefined : formatCartAmount(totalPriceDiscounted, t),
  }
}

export function getLineItemInfo(args: GetLineItemInfoArgs) {
  const { lineItem, country, lang, t, quantity } = args
  const localeCode = getLocaleCodeFromCountryLang(country, lang)

  if (!lineItem) return undefined

  const id = lineItem.id
  const [img] = getLineItemImages(lineItem, localeCode, {
    tags: ['type:sku', 'priority:1', `locale:${localeCode}`],
    fallbackTags: ['default'],
  })

  const name = getLineItemName(lineItem, localeCode)
  const computedDetails = getComputedDetailsLabels(lineItem, localeCode)
  const marketingDetails = getLineItemAttribute(lineItem, 'marketingDetails', localeCode)
  const canadianTaxLabel = getCanadianTaxLabel(lineItem, localeCode)

  const { discountNames, cartDiscountNames, productDiscountName } = getLineItemDiscountNames(
    lineItem,
    localeCode as LocaleCode
  )

  const { totalPriceFormatted, totalPriceDiscountedFormatted, totalPrice, totalPriceDiscounted } =
    getLineItemPrices({
      lineItem,
      t,
      quantity,
    })

  const info = {
    id,
    img,
    cartDiscountNames,
    productDiscountName,
    discountNames,
    name,
    computedDetails,
    marketingDetails,
    canadianTaxLabel,
    totalPrice: totalPriceFormatted,
    totalPriceDiscounted: totalPriceDiscountedFormatted,
    totalPriceCents: totalPrice,
    totalPriceDiscountedCents: totalPriceDiscounted,
  }

  return info
}

/**
 * Returns the discount keys for a line item.
 * Used to track discounts in analytics.
 */
export function getLineItemDiscountKeys(lineItem: Commercetools.LineItem) {
  const productDiscountKey = lineItem.price?.discounted?.discount?.obj?.key

  const cartDiscountKeys = lineItem.discountedPricePerQuantity
    .map(({ discountedPrice }) => discountedPrice?.includedDiscounts)
    .flat()
    .map(({ discount }) => (discount as CartDiscountReference)?.obj?.key)
    .filter(Boolean) as string[]

  const discountKeys = Array.from(
    new Set([productDiscountKey, ...cartDiscountKeys]).values()
  ).filter(Boolean) as string[]

  return { discountKeys, productDiscountKey, cartDiscountKeys }
}

/**
 * Returns the discount keys for all line items in a cart.
 * Used to track discounts in analytics.
 */
export function getLineItemsDiscountKeys(lineItems: Commercetools.LineItem[] | undefined | null) {
  const discountKeys = lineItems
    ?.map((lineItem) => getLineItemDiscountKeys(lineItem).discountKeys)
    .flat()
    .filter(Boolean) as string[]

  return Array.from(new Set(discountKeys).values())
}

/**
 * Returns the discount names for a line item.
 * Used to display discount names on the cart, checkout and order-confirmation pages.
 */
export function getLineItemDiscountNames(lineItem: Commercetools.LineItem, localeCode: LocaleCode) {
  const productDiscountName = lineItem?.price?.discounted?.discount?.obj?.description?.[localeCode]

  const cartDiscountNames = lineItem.discountedPricePerQuantity
    .map(({ discountedPrice }) => discountedPrice?.includedDiscounts)
    .flat()
    .map(({ discount }) => (discount as CartDiscountReference)?.obj?.description?.[localeCode])
    .filter(Boolean) as string[]

  const discountNames = Array.from(
    new Set([productDiscountName, ...cartDiscountNames]).values()
  ).filter(Boolean) as string[]

  return { discountNames, productDiscountName, cartDiscountNames }
}
